import React, {useEffect} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import MarketingHeader from './marketing/MarketingHeader';
import MarketingFooter from './marketing/MarketingFooter';
import {signOut as signOutThunk} from '../store/slices/authThunks';
import {isAuthenticatedSelector} from '../store/selectors/auth.selectors';
import {useToast} from './Toast';
import {findPublicPage} from '../../config/publicPages';

/**
 * Chrome for the public pages (landing, docs, terms, contact).
 *
 * These scroll normally, unlike the signed-in app shell which is a fixed-height flex layout.
 */
const MarketingLayoutContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const {pathname, hash} = useLocation();

  // BrowserRouter never resets scroll on its own — a `<Link>` from the bottom of a long page
  // (e.g. docs' "Get in touch") lands on the next page still scrolled to that same offset instead
  // of the top. It doesn't follow a hash either, so the footer's `/#features` and `/#pricing` are
  // scrolled to here — from any public page, and again when clicked while already on `/`. In-page
  // anchors (docs' own "On this page" nav) handle their own scrollIntoView without touching the hash.
  useEffect(() => {
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    if (target) {
      target.scrollIntoView({behavior: 'smooth', block: 'start'});
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  // The prerendered HTML carries each page's title, but client-side navigation between public pages
  // never reloads the document, so it has to be kept in step here.
  useEffect(() => {
    const page = findPublicPage(pathname);
    if (page) {
      document.title = page.title;
    }
  }, [pathname]);

  const handleSignOut = async () => {
    try {
      await dispatch(signOutThunk()).unwrap();
      showToast('Signed out successfully', 'success');
    } catch (error: any) {
      showToast(`Failed to sign out: ${error}`, 'error');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-900">
      <MarketingHeader isAuthenticated={isAuthenticated} onSignOut={handleSignOut} />
      <main className="flex-1 w-full">
        <Outlet />
      </main>
      <MarketingFooter />
    </div>
  );
};

export default MarketingLayoutContainer;
